import { useFetchMovie, useFetchSessionTypes } from "@multicines/services";
import { useCountriesStore } from "@multicines/stores";
import { useMoviesStore } from "@multicines/stores";
import { Modality, SessionType as ISessionType } from "@multicines/types";
import { getCalendarDays, titleCase } from "@multicines/utils";
import { getApiMessageError, getTradeCodeApiError } from "@multicines/utils";
import dayjs from "dayjs";
import EmptySVG from "images/empty-movie.svg";
import MegaphoneSVG from "images/megaphone.svg";
import PlaySVG from "images/play.svg";
import { useRouter } from "next/router";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getMovieStatusList, getTabs } from "./MovieDetail.helpers";
import Styles from "./MovieDetail.styles";
import { MovieTabsOption } from "./MovieDetail.types";
import { MovieDetailProps as Props } from "./MovieDetail.types";
import MovieDetailPlaceholder from "./MovieDetailPlaceholder/MovieDetailPlaceholder";
import useMovieDetailAnalytics from "./useMovieDetailAnalytics";
import MovieDetailFilter from "../MovieDetailFilter/MovieDetailFilter";
import MovieInformation from "../MovieInformation/MovieInformation";
import Button from "components/global/Button/Button";
import Days from "components/global/Days/Days";
import EmptyState from "components/global/EmptyState/EmptyState";
import ErrorState from "components/global/ErrorState/ErrorState";
import Footer from "components/global/Footer/Footer";
import GoBack from "components/global/GoBack/GoBack";
import MovieCard from "components/global/MovieCard/MovieCard";
import Navbar from "components/global/Navbar/Navbar";
import ScrollableTabs from "components/global/ScrollableTabs/ScrollableTabs";
import SessionType from "components/global/SessionType/SessionType";
import SessionTypePlaceholder from "components/global/SessionType/SessionTypePlaceholder/SessionTypePlaceholder";
import StatusLabel from "components/global/StatusLabel/StatusLabel";
import CONSTANTS from "config/constants";
import useI18n from "hooks/useI18n";
import { useAuthStore } from "stores/auth/auth.store";
import { useStoresStore } from "stores/stores/stores.store";
import { sanitizeQueryParams } from "utils/common.utils";

import "dayjs/locale/es";

const { DAYS } = CONSTANTS.DATE;

const MovieDetail: React.FC<Props> = props => {
  const { query, push } = useRouter();
  const { movieId = "" } = sanitizeQueryParams(query);
  const { modality: movieModality = "All" } = sanitizeQueryParams(query);
  const modalities =
    !movieModality?.trim() || movieModality.trim().length <= 1
      ? "All"
      : (movieModality as Modality);
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const selectedStore = useStoresStore(store => store.selectedStore);
  const { data: movie, isSuccess: isSuccessFetchMovie } = useFetchMovie(
    { isAnonymous, uid },
    selectedStore,
    movieId,
    modalities
  );
  const fetchSessionTypes = useFetchSessionTypes(
    { isAnonymous, uid },
    selectedStore,
    movieId
  );
  const t = useI18n();
  const setSelectedMovie = useMoviesStore(store => store.setSelectedMovie);
  const selectedDate = useMoviesStore(store => store.selectedDate);
  const setSelectedDate = useMoviesStore(store => store.setSelectedDate);
  const selectedCountry = useCountriesStore(state => state.selectedCountry);
  const [activeTab, setActiveTab] = useState<MovieTabsOption>("screenings");
  const [language, setLanguage] = useState<string | undefined>();
  const [room, setRoom] = useState<string | undefined>();
  const [format, setFormat] = useState<string | undefined>();

  const { title: movieTitle } = movie ?? {};
  const { logSelectTrailer } = useMovieDetailAnalytics({
    isSuccessFetchMovie,
    movieTitle,
    activeTab
  });

  const { data: sessionTypes, isFetching, refetch } = fetchSessionTypes;
  const { status, error } = fetchSessionTypes;

  const [ticketsStore] = selectedStore ?? [];
  const sessionTypeCount = sessionTypes?.length ?? 0;
  const noSessionTypes = !!sessionTypes && sessionTypes.length === 0;
  const { storeName = "" } = ticketsStore ?? {};

  const { preReleaseDate } = movie ?? {};

  const { imageUrl, trailerUrl } = movie ?? {};
  const { bannerUrl, modality } = movie ?? {};

  const days = useMemo(() => getCalendarDays(DAYS), []);
  const { locale } = selectedCountry;
  const displayMonth = dayjs(selectedDate)?.locale(locale).format("MMMM");
  const notFunctions = activeTab !== "screenings";
  const isPreview = modality === "Preview";
  const backgroundImage = !bannerUrl ? imageUrl : bannerUrl;

  const renderItem = useCallback(
    (listItem: ISessionType, index: number) => {
      const sessionType = listItem;

      return (
        <SessionType
          key={index}
          position={index}
          sessionType={sessionType}
          sessionTypeCount={sessionTypeCount}
          disabled={isFetching}
        />
      );
    },
    [isFetching, sessionTypeCount]
  );

  const refetchSessionTypesQuery = async () => {
    await refetch();
  };

  const emptyHandler = () => {
    const nextDate = days.find(day =>
      day.isSame(selectedDate?.add(1, "day"), "day")
    );
    if (nextDate) {
      setSelectedDate(nextDate);
      return;
    }
    push("/billboard");
  };

  const renderItemMemoized = useMemo(() => renderItem, [renderItem]);

  const filterRooms = (array: ISessionType[] | undefined, name: string) => {
    if (!array || !name || name === t.movie.allRooms) return array;
    const filtered = array
      ?.map(item => {
        const filteredData = item.theaterTypes.filter(
          theaterType => theaterType.name === name
        );
        return { name: item.name, theaterTypes: filteredData };
      })
      .filter(item => item.theaterTypes.length);
    return filtered;
  };

  const filterOthers = (array: ISessionType[] | undefined, name: string) => {
    if (
      !array ||
      !name ||
      name === t.movie.allLanguages ||
      name === t.movie.allFormats
    )
      return array;

    const filtered = array
      ?.map(item => {
        const theaterTypes = item.theaterTypes
          .map(theaterType => {
            const sessions = theaterType.sessions.filter(session =>
              session.sessionAttributesNames.includes(name)
            );
            return { ...theaterType, sessions };
          })
          .filter(theaterType => theaterType.sessions.length);
        return {
          ...item,
          theaterTypes
        };
      })
      .filter(theaterType => theaterType.theaterTypes.length);

    return filtered;
  };

  const renderScreenings = () => {
    return (
      <>
        <MovieDetailFilter
          movie={movie}
          sessionTypes={sessionTypes}
          setLanguage={setLanguage}
          setRoom={setRoom}
          setFormat={setFormat}
          language={language}
          format={format}
          room={room}
        />
        <div className="MovieDetail__content__days">
          <p className="MovieDetail__content__sessions">
            {titleCase(displayMonth)}
          </p>
          <Days isReady={isSuccessFetchMovie} preReleaseDate={preReleaseDate} />
        </div>
        <div className="MovieDetail__status-wrapper">
          {getMovieStatusList(t).map((item, index) => {
            const { backgroundColor, text } = item;
            return (
              <StatusLabel
                key={index}
                text={text}
                backgroundColor={backgroundColor}
              />
            );
          })}
        </div>
        <p className="MovieDetail__content__session-type-text">
          {titleCase(storeName)}
        </p>
      </>
    );
  };

  const trailerHandler = () => {
    logSelectTrailer();
  };

  const renderTrailers = () => {
    return (
      <Button
        mode="SECONDARY"
        className="MovieDetail__buttons__trailer"
        onClick={trailerHandler}
      >
        <a href={trailerUrl} target="_blank" rel="noreferrer">
          <PlaySVG />
          {t.common.trailer}
        </a>
      </Button>
    );
  };

  const renderInformation = () => {
    return (
      <MovieInformation
        genre={movie?.genre}
        actors={movie?.actors}
        synopsis={movie?.synopsis}
        ageRestriction={movie?.ageRestriction}
      />
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "screenings":
        return renderScreenings();
      case "trailers":
        return renderTrailers();
      case "information":
        return renderInformation();
      default:
        return null;
    }
  };

  const header = (
    <div className="MovieDetail__wrapper">
      <div className="MovieDetail__wrapper__header">
        <p className="MovieDetail__wrapper__title">{t.movie.screenings}</p>
        <ScrollableTabs<MovieTabsOption>
          className="MovieDetail__tabs"
          options={getTabs(t)}
          onChange={name => setActiveTab(name)}
        />
        {renderContent()}
      </div>
    </div>
  );

  const filtered = filterRooms(
    filterOthers(filterOthers(sessionTypes, language!), format!),
    room!
  );

  const renderStates = (
    <>
      {status === "loading" || status === "idle" ? (
        <SessionTypePlaceholder />
      ) : null}

      {status === "error" ? (
        <ErrorState
          title={getTradeCodeApiError(error, "TSE", true)}
          description={getApiMessageError(error)}
          onClick={refetchSessionTypesQuery}
        />
      ) : null}

      {status === "success" && noSessionTypes && isPreview ? (
        <EmptyState
          icon={<MegaphoneSVG />}
          title={t.movie.previewTitle}
          description={t.movie.previewDescription}
        />
      ) : null}

      {status === "success" && noSessionTypes && !isPreview ? (
        <EmptyState
          icon={<EmptySVG />}
          description={t.movie.emptyMessage}
          buttonTitle={t.movie.nextAvailableDate}
          onClick={emptyHandler}
        />
      ) : null}

      {status === "success" && !noSessionTypes
        ? filtered?.map((sessionType, index) => {
            return renderItemMemoized(sessionType, index);
          })
        : null}
    </>
  );

  useEffect(() => {
    if (!movie) return;
    setSelectedMovie(movie);
  }, [movie, setSelectedMovie]);

  return (
    <Styles
      className="MovieDetail"
      image={backgroundImage}
      notFunctions={notFunctions}
    >
      <header className="MovieDetail__header">
        <Navbar showBrand />
      </header>
      <div className="MovieDetail__background" />
      <main className="MovieDetail__main">
        <div className="MovieDetail__main__header">
          <GoBack className="MovieDetail__go-back-desktop" title="Regresar" />
        </div>
        <div className="MovieDetail__content">
          {movie ? (
            <div className="MovieDetail__content__card">
              <MovieCard movie={movie} isSelected />
              <div className="MovieDetail__content__trailer">
                {trailerUrl ? renderTrailers() : null}
              </div>
              <div className="MovieDetail__information">
                {renderInformation()}
              </div>
            </div>
          ) : (
            <MovieDetailPlaceholder />
          )}
          <div className="MovieDetail__content__session">
            {header}
            <div className="MovieDetail__content__session-type">
              {activeTab === "screenings" ? renderStates : null}
            </div>
          </div>
        </div>
      </main>
      <footer className="MovieDetail__footer">
        <Footer />
      </footer>
    </Styles>
  );
};

export default MovieDetail;
